import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';

export type EntitlementState = 'none' | 'purchased' | 'scheduled' | 'active' | 'expired' | 'trial';

export interface CycleInfo {
  cycleName: string;
  cycleKey: string;
  cycleStartDate: string;
  examDate: string;
}

export interface BarPackageAccess {
  hasAccess: boolean;
  /** @deprecated Use hasAccess instead */
  hasBarPackage: boolean;
  entitlementState: EntitlementState;
  cycleInfo: CycleInfo | null;
  expiresAt: string | null;
  isLoading: boolean;
}

/**
 * Derive a human-readable cycle name from an exam_cycle key.
 * e.g. '2026-07' → 'July 2026', '2027-02' → 'February 2027'
 */
function deriveCycleName(examCycle: string): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const parts = examCycle.split('-');
  if (parts.length >= 2) {
    const year = parts[0];
    const monthIndex = parseInt(parts[1], 10) - 1;
    if (monthIndex >= 0 && monthIndex < 12) {
      return `${months[monthIndex]} ${year}`;
    }
  }
  return examCycle;
}

/**
 * Extended row type for user_entitlements including cycle columns.
 * These columns may not yet exist in the generated types but will be
 * added by the activation migration. PostgREST will return them as
 * additional fields when present on the table.
 */
interface EntitlementRow {
  id: string;
  status: string | null;
  state: string | null;
  expires_at: string | null;
  exam_cycle?: string | null;
  cycle_start_date?: string | null;
  exam_date?: string | null;
}

/**
 * Check if the current user has a bar_package entitlement and determine its state.
 * Used to gate NextGen bar prep content and control routing/banners.
 */
export function useBarPackageAccess(): BarPackageAccess {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['bar-package-access', user?.id],
    queryFn: async () => {
      // Select base columns that exist in generated types.
      // Cycle columns (exam_cycle, cycle_start_date, exam_date) will be
      // returned by PostgREST when present on the table even though they
      // aren't in the generated TS types yet.
      const { data: rawData, error } = await supabase
        .from('user_entitlements')
        .select('id, status, state, expires_at, exam_cycle, cycle_start_date, exam_date')
        .eq('user_id', user!.id)
        .eq('feature', 'bar_package')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (!rawData) {
        return {
          hasAccess: false,
          entitlementState: 'none' as EntitlementState,
          cycleInfo: null,
          expiresAt: null,
        };
      }

      // Cast to our extended type to access cycle columns if they exist
      const data = rawData as unknown as EntitlementRow;

      // Map the lifecycle column to our EntitlementState type.
      // Prefer `state` (the newer lifecycle column added 2026-04-15) over legacy `status`.
      // Why: trial users are written with status='active' (CHECK-valid) + state='trial';
      // reading status would mis-classify them as 'active' and hide the trial banner.
      // Fall back to `status` for legacy rows where `state` is null.
      const lifecycleValue = (data.state ?? data.status ?? '') as string;
      let entitlementState: EntitlementState;
      if (lifecycleValue === 'active' || lifecycleValue === 'purchased' || lifecycleValue === 'scheduled' || lifecycleValue === 'expired' || lifecycleValue === 'trial') {
        entitlementState = lifecycleValue;
      } else {
        // Unknown state — treat as none
        entitlementState = 'none';
      }

      // Check expiration: if active or trial but expired, override state
      const isExpired = !!data.expires_at && new Date(data.expires_at) < new Date();
      if (isExpired && (entitlementState === 'active' || entitlementState === 'trial')) {
        entitlementState = 'expired';
      }

      const hasAccess = (entitlementState === 'active' || entitlementState === 'trial') && !isExpired;

      // Build cycle info if the user has selected an exam cycle
      let cycleInfo: CycleInfo | null = null;
      if (data.exam_cycle) {
        cycleInfo = {
          cycleName: deriveCycleName(data.exam_cycle),
          cycleKey: data.exam_cycle,
          cycleStartDate: data.cycle_start_date ?? '',
          examDate: data.exam_date ?? '',
        };
      }

      return {
        hasAccess,
        entitlementState,
        cycleInfo,
        expiresAt: data.expires_at ?? null,
      };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    hasAccess: query.data?.hasAccess ?? false,
    hasBarPackage: query.data?.hasAccess ?? false,
    entitlementState: query.data?.entitlementState ?? 'none',
    cycleInfo: query.data?.cycleInfo ?? null,
    expiresAt: query.data?.expiresAt ?? null,
    isLoading: query.isLoading,
  };
}
